// Lot 746 — une publication partagée dans plusieurs groupes n'apparaît qu'UNE fois sur le fil (décision owner : « une
// seule carte, avec la liste des groupes », tous les partageurs regroupés). Chaque partage en groupe est une
// publication distincte côté serveur ({ group, shared_post }) : on garde la première carte rencontrée (la plus récente
// — ou l'original s'il est déjà affiché) et on y accroche `group_shares` { groups, sharers, post_ids, count }.
// Pur JS, sans réseau : appliqué à chaque page reçue (Home) et au fil mis en cache.

const origIdOf = (p) => (p?.group && p?.shared_post?.id ? p.shared_post.id : null);
// Lot 755 — même publication ÉCRITE directement dans plusieurs groupes par le même auteur (pas un partage : textes, fond,
// médias identiques) → une seule carte aussi, « dans N groupes : A, B, C ». Clé = auteur + contenu.
// Lot 756 — « Publier dans plusieurs groupes » : les copies portent `batch_id` → clé exacte, publication personnelle comprise.
const dupKeyOf = (p) => {
  if (p?.shared_post || !p?.author?.id) return null;
  if (p.batch_id) return `batch:${p.batch_id}`;
  if (!p.group) return null;
  const body = [(p.content || "").trim().toLowerCase(), p.bg || "", (p.images || []).join("|"), p.video_url || p.hls_url || "", p.audio_url || ""].join("\u0001");
  return body.replace(/\u0001/g, "") ? `dup:${p.author.id}:${body}` : null;
};
const keyOf = (p) => origIdOf(p) || dupKeyOf(p) || p?.id;

const entryOf = (p) => ({
  post_id: p.id,
  group: p.group ? { id: p.group.id, name: p.group.name, privacy: p.group.privacy } : null,
  sharer: p.author?.id ? { id: p.author.id, name: p.author.name } : null,
});

const attach = (rep, entry, kind) => {
  const cur = rep.group_shares || { groups: [], sharers: [], post_ids: [], count: 0 };
  if (cur.post_ids.includes(entry.post_id)) return rep;
  const groups = entry.group && !cur.groups.some((g) => g.id === entry.group.id) ? [...cur.groups, entry.group] : cur.groups;
  const sharers = entry.sharer && !cur.sharers.some((s) => s.id === entry.sharer.id) ? [...cur.sharers, entry.sharer] : cur.sharers;
  return { ...rep, group_shares: { ...cur, groups, sharers, post_ids: [...cur.post_ids, entry.post_id], count: groups.length, ...(kind ? { kind } : {}) } };
};

// Tous les identifiants représentés à l'écran (cartes + partages fusionnés) — pour « n nouvelles publications ».
export const knownIdSet = (posts) => {
  const s = new Set();
  for (const p of posts || []) { s.add(p.id); for (const id of p.group_shares?.post_ids || []) s.add(id); }
  return s;
};

// Ajoute une page au fil en fusionnant les partages en groupe ; `on=false` → simple ajout sans doublon (garde-fou).
export const mergeFeedPages = (prev, incoming, on = true) => {
  const out = [...(prev || [])];
  const known = knownIdSet(out);
  if (!on) return [...out, ...(incoming || []).filter((p) => p?.id && !known.has(p.id))];
  const index = new Map();
  out.forEach((p, i) => { if (p?.id && !index.has(keyOf(p))) index.set(keyOf(p), i); });
  for (const q of incoming || []) {
    if (!q?.id || known.has(q.id)) continue;
    known.add(q.id);
    const key = keyOf(q);
    const i = index.get(key);
    if (origIdOf(q)) {
      if (i === undefined) { index.set(key, out.length); out.push(attach(q, entryOf(q))); } else out[i] = attach(out[i], entryOf(q));
    } else if (dupKeyOf(q)) {
      // Lot 755 — doublon écrit dans un autre groupe : la 1re carte (la plus récente) porte la liste des groupes.
      if (i === undefined) { index.set(key, out.length); out.push(attach(q, entryOf(q), "dup")); } else out[i] = attach(out[i], entryOf(q), "dup");
    } else if (i === undefined) {
      index.set(key, out.length); out.push(q);
    } else {
      out[i] = { ...out[i], group_shares: { ...(out[i].group_shares || { groups: [], sharers: [], count: 0 }), post_ids: [...(out[i].group_shares?.post_ids || []), q.id], original_seen: true } };
    }
  }
  return out;
};

export const collapseGroupShares = (list, on = true) => mergeFeedPages([], list, on);

// Rafraîchissement sur place (fil descendu) : nouvelles données par id, en gardant les partages déjà fusionnés.
export const refreshInPlace = (cur, fresh) => {
  const map = new Map((fresh || []).map((p) => [p.id, p]));
  return (cur || []).map((p) => (map.has(p.id) ? { ...map.get(p.id), ...(p.group_shares ? { group_shares: p.group_shares } : {}) } : p));
};
